import { SimpleGrid } from "@chakra-ui/react";
import { FillImage } from "components/images";
import React from "react";
import { MediaAccordion } from "ui/accordion";
import { type AccordionItemContent } from "ui/accordion/accordion-item";
import { TargetIcon, LockIcon, EyeSlashIcon } from "ui/icons";
import { SolidPlate } from "ui/plates";
import { Rule } from "ui/rule";
import { TextColumn, TextTitle } from "ui/text";
import firstAccordionImage from "../../../../public/assets/platform/reverse-etl/accordion-1.png";
import secondAccordionImage from "../../../../public/assets/platform/reverse-etl/accordion-2.png";
import thirdAccordionImage from "../../../../public/assets/platform/reverse-etl/accordion-3.png";
import fourthAccordionImage from "../../../../public/assets/platform/reverse-etl/accordion-4.png";

const accordionItems: AccordionItemContent[] = [
  {
    title: "Role-based access control",
    content:
      "Grant secure access to data sources and destinations with role-based permissions that are easy to configure, while offering the flexibility to support large enterprises with multiple teams, regional divisions, and complex brand structures.",
    media: (
      <FillImage
        src={firstAccordionImage}
        alt="Abstract illustration of Hightouch UI managing granular access for different user groups like Marketing and Engineering"
      />
    ),
  },
  {
    title: "Approval flows and sync templates",
    content:
      "Prevent syncs from going live without admin approval, or enforce the use of pre-approved sync templates. ",
    media: (
      <FillImage
        src={secondAccordionImage}
        alt="Abstract illustration of Hightouch UI with an approval modal open. Two reviewers Luca and Sophia are selected with a custom message explaining context for the review request."
      />
    ),
  },
  {
    title: "Staging environments",
    content: "Test changes in a staging workspace, then deploy to production with a single click.",
    media: (
      <FillImage
        src={thirdAccordionImage}
        alt="Abstract illustration of Staging and Development environments to independently run syncs and deploy."
      />
    ),
  },
  {
    title: "Folders, labels, and descriptions",
    content:
      "Keep your data models organized and well-documented for easy reuse across the business. Automatically import column descriptions and other metadata from your warehouse.",
    media: (
      <FillImage
        src={fourthAccordionImage}
        alt="Abstract illustration depicting how model metadata from Snowflake can be imported into various Marketing campaigns"
      />
    ),
  },
];

export function GovernanceAndSecurity() {
  return (
    <SolidPlate id="data-governance" theme="dark" bgColor="new.green.950" spotlightColor="new.green.900">
      <TextTitle
        align="center"
        theme="dark"
        size="xl"
        title="Enterprise-grade governance"
        description="Hightouch makes it easy for teams to collaborate across your business, without sacrificing control or compliance."
      />

      <MediaAccordion theme="dark" variant="dynamic" items={accordionItems} mt={{ base: "big", md: "huge" }} />

      <Rule theme="dark" py={{ base: "biggest", md: "huge", xl: "huger" }} />

      <TextTitle align="center" theme="dark" size="lg" title="Secure by design" />

      <SimpleGrid
        columns={{ base: 1, md: 3 }}
        gap="bigger"
        mt={{ base: "biggest", md: "huge" }}
        mb={{ base: "small", md: "big" }}
      >
        <TextColumn
          theme="dark"
          iconChip={{ variant: "blue", icon: EyeSlashIcon }}
          titleAs="h3"
          title="Hightouch never stores your data"
          description="We don’t store a single byte of your customer data (not even logs). Bring your own bucket and keep all storage at rest in your own infrastructure."
        />

        <TextColumn
          theme="dark"
          iconChip={{ variant: "yellow", icon: TargetIcon }}
          titleAs="h3"
          title="Choose your region and cloud"
          description="Hightouch syncs are processed by ephemeral workers that live in a cloud of your choice. We support AWS, GCP, and Azure."
        />

        <TextColumn
          theme="dark"
          iconChip={{ variant: "green", icon: LockIcon }}
          titleAs="h3"
          title="Private networking"
          description="Connect securely using AWS PrivateLink, GCP Private Service Connect, or Azure Private Link, with SSH tunneling also available for on-prem environments."
        />
      </SimpleGrid>
    </SolidPlate>
  );
}
